import * as Sentry from '@sentry/react';
import { getCurrentConfig } from '../utils/environmentConfig';

export interface SentryConfig {
  dsn: string;
  environment: string;
  enableTracing: boolean;
  sampleRate: number;
  tracesSampleRate: number;
  debug: boolean;
}

/**
 * Sentry Configuration for Deals Website
 * Environment-specific error tracking and performance monitoring
 */
export const sentryConfig: SentryConfig = {
  // Get DSN from environment variable
  dsn: process.env.REACT_APP_SENTRY_DSN || '',

  environment: process.env.NODE_ENV || 'development',

  // Enable performance monitoring in production
  enableTracing: process.env.NODE_ENV === 'production',

  // Sample rate for error events (1.0 = 100%)
  sampleRate: 1.0, // Capture all errors in both dev and production

  // Sample rate for performance monitoring (0.1 = 10% for cost efficiency)
  tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 0.5,

  // Enable debug logging in development
  debug: process.env.NODE_ENV === 'development',
};

/**
 * Initialize Sentry error tracking with environment-aware configuration
 */
export const initializeSentry = () => {
  const config = getCurrentConfig();

  // Disable Sentry in local network development
  if (config.isLocalNetwork && config.isDevelopment) {
    // Sentry disabled for local network development
    return;
  }

  // Only initialize Sentry in production environments
  if (config.isProduction) {
    Sentry.init({
      dsn:
        process.env.REACT_APP_SENTRY_DSN ||
        'https://0adaecae9efefcd6c70a6a90ca224f11@o4509483988090880.ingest.us.sentry.io/4509484427247616',
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      environment: config.environment,

      // Performance Monitoring
      tracesSampleRate: config.isProduction ? 0.1 : 1.0,

      // Session Replay
      replaysSessionSampleRate: config.isProduction ? 0.1 : 1.0,
      replaysOnErrorSampleRate: 1.0,

      // Enhanced error filtering
      beforeSend(event, hint) {
        // Skip non-critical errors in development
        if (config.isDevelopment) {
          const isNonCritical =
            event.message?.includes('Sentry initialization') ||
            event.message?.includes('WebSocket') ||
            event.message?.includes('analytics');
          if (isNonCritical) return null;
        }

        return event;
      },

      // Environment-based URL filtering
      allowUrls: (() => {
        const urls = [];

        // Add current domain based on environment
        if (config.isProduction) {
          // Production domains
          if (config.domain.includes('.web.app') || config.domain.includes('.firebaseapp.com')) {
            urls.push(`https://${config.domain}/`);
          }
          // Allow current production domain
          urls.push(`https://${config.domain}/`);
        } else {
          // Development - allow current host
          urls.push(`${config.protocol}://${config.domain}:${config.port}/`);

          // Allow local network access patterns
          if (config.isLocalNetwork) {
            urls.push(`${config.protocol}://${config.domain}:${config.port}/`);
          }
        }

        return urls;
      })(),

      // Reduce logging noise
      debug: false,
    });

    // Sentry initialized successfully
    // Sentry configuration logged
  } else {
    // Sentry disabled for development environment
  }
};

/**
 * Enhanced error reporting with context
 */
export const reportError = (
  error: Error,
  context?: Record<string, any>,
  level: Sentry.SeverityLevel = 'error'
): void => {
  if (process.env.NODE_ENV === 'development') {
    // 🐛 Error reported to Sentry: error, context
  }

  Sentry.withScope((scope) => {
    scope.setLevel(level);

    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
    }

    scope.setTag('component', 'error-tracking');
    Sentry.captureException(error);
  });
};

/**
 * Report custom events for business logic tracking
 */
export const reportEvent = (
  message: string,
  context?: Record<string, any>,
  level: Sentry.SeverityLevel = 'info'
): void => {
  Sentry.withScope((scope) => {
    scope.setLevel(level);

    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
    }

    Sentry.captureMessage(message);
  });
};

/**
 * Set user context for error tracking
 */
export const setUserContext = (user: {
  id?: string;
  email?: string;
  username?: string;
  isAuthenticated?: boolean;
}): void => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
    isAuthenticated: user.isAuthenticated,
  });
};

/**
 * Add breadcrumb for debugging context
 */
export const addBreadcrumb = (
  message: string,
  category: string = 'user-action',
  level: Sentry.SeverityLevel = 'info',
  data?: Record<string, any>
): void => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
};

// React Router dependencies for tracing are imported at the top
